// src/components/UpgradeBanner.jsx
// Banner shown when seat limit is reached, prompting user to upgrade

import React from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { useSubscription } from "../context/SubscriptionContext";

/**
 * UpgradeBanner - Gradient banner prompting users to upgrade
 *
 * Shown when:
 * - Seat limit is reached (can_add_members = false)
 * - Or custom condition via props
 *
 * @param {Object} props
 * @param {string} props.title - Custom title (optional)
 * @param {string} props.description - Custom description (optional)
 * @param {boolean} props.show - Force show/hide (optional)
 */
export default function UpgradeBanner({
  title,
  description,
  show,
}) {
  const navigate = useNavigate();
  const { seats } = useSubscription();

  // Determine visibility
  const shouldShow = show !== undefined ? show : !seats?.can_add_members;

  if (!shouldShow) return null;

  const defaultTitle = "Need more space for your team?";
  const defaultDescription = "Upgrade your plan to add more team members and unlock additional features.";

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)",
        borderRadius: 2,
        p: 3,
        mb: 3,
        color: "white",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <RocketLaunchRoundedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {title || defaultTitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.9, mt: 0.5 }}
            >
              {description || defaultDescription}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          onClick={() => navigate("/pricing")}
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            bgcolor: "rgba(255,255,255,0.95)",
            color: "#6366F1",
            fontWeight: 700,
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: "white",
            },
          }}
        >
          View Plans
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Compact version of UpgradeBanner for inline use
 */
export function UpgradeBannerCompact() {
  const navigate = useNavigate();
  const { seats } = useSubscription();

  if (seats?.can_add_members) return null;

  return (
    <Box
      sx={{
        bgcolor: "warning.lighter",
        border: "1px solid",
        borderColor: "warning.light",
        borderRadius: 1.5,
        p: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Typography variant="body2" color="warning.dark">
        You've reached the seat limit for your plan.
      </Typography>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        onClick={() => navigate("/pricing")}
      >
        Upgrade
      </Button>
    </Box>
  );
}
